import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  PREFERENCES_OPTIONS,
  IPreferencesOptions,
} from '@core/constants/multi-selectors';
import { IFilterCBSAs } from '@shared/models/filters';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { setupAccountAction } from '@state/auth/auth.actions';
import {
  selectAuthUser,
  selectEntityId,
  selectAuthLoading,
} from '@state/auth/auth.selectors';
import { AccountSetupMarketsComponent } from './markets/markets.component';

@UntilDestroy()
@Component({
  selector: 'app-account-setup-modal-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class AccountSetupFormComponent implements OnInit, AfterViewInit {
  public step = 1;
  protected loading = false;
  protected focused = '';
  protected userFirstName: string | undefined;
  protected viewReady = false;
  // if undefined show as loading
  protected skipStep1: boolean | undefined = false;

  @ViewChild(AccountSetupMarketsComponent)
  marketsComponent?: AccountSetupMarketsComponent;

  public accountSetupForm: FormGroup = this.formBuilder.group({
    roles: new FormControl([], [Validators.required]),
    investmentStyles: new FormControl([], []),
    rehabComfortabilities: new FormControl([], []),
    entities: new FormControl([], [Validators.required]),
    cbsaPreferences: new FormControl([], [Validators.required]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef
  ) {}

  protected options: IPreferencesOptions = {
    ...PREFERENCES_OPTIONS,
    submarkets: [],
  };

  ngOnInit(): void {
    this.store
      .select(selectAuthLoading)
      .pipe(untilDestroyed(this))
      .subscribe((loading) => {
        this.loading = loading;
        if (loading) {
          this.accountSetupForm.disable();
        } else {
          this.accountSetupForm.enable();
        }
      });

    // Check if entityId was passed via URL parameter (e.g., from referrer link)
    // If present, pre-populate the entities form field and skip to step 2
    // Otherwise, start from step 1 for normal account setup flow
    this.store
      .select(selectEntityId)
      .pipe(untilDestroyed(this))
      .subscribe((entityId) => {
        // Defer updates to next tick to avoid dev-mode NG0100
        setTimeout(() => {
          const nextStep = entityId ? 2 : 1;
          if (entityId) {
            this.accountSetupForm.controls['entities'].setValue([
              {
                id: entityId,
              },
            ]);
            this.skipStep1 = true;
          } else {
            this.skipStep1 = false;
          }
          // Only override step if it's still at the default
          if (this.step === 1) {
            this.step = nextStep;
          }
          // Ensure the view reflects deferred updates
          this.cdr.detectChanges();
        });
      });

    this.store
      .select(selectAuthUser)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        if (user) {
          this.userFirstName = user.firstName;

          // Pre-populate cbsa preferences on next tick to avoid NG0100
          if (user.cbsaPreferences && user.cbsaPreferences.length > 0) {
            setTimeout(() => {
              this.accountSetupForm.controls['cbsaPreferences'].setValue(
                user.cbsaPreferences.map((cbsa) => ({
                  type: cbsa.type?.toLowerCase(),
                  preference: cbsa.preference,
                }))
              );
              this.cdr.detectChanges();
            });
          }
        }
      });
  }

  ngAfterViewInit(): void {
    // Guard bindings that can change during initial render
    Promise.resolve().then(() => {
      this.viewReady = true;
      this.cdr.detectChanges();
    });
  }

  submitHandler() {
    if (this.accountSetupForm.invalid) {
      this.accountSetupForm.markAllAsTouched();
      return;
    }
    const values = this.accountSetupForm.value;
    if (typeof values.investmentStyles === 'string') {
      values.investmentStyles = [];
    }
    if (typeof values.rehabComfortabilities === 'string') {
      values.rehabComfortabilities = [];
    }
    // transform each entity into a json
    values.entities = values.entities.map(
      (entity: { id?: string; full_name: string }) => JSON.stringify(entity)
    );
    // transform each cbsa preference into a json
    values.cbsaPreferences = values.cbsaPreferences.map((cbsa: IFilterCBSAs) =>
      JSON.stringify({ type: cbsa.type, preference: cbsa.preference })
    );

    this.store.dispatch(
      setupAccountAction({
        input: {
          roles: values.roles,
          investmentStyles: values.investmentStyles,
          rehabComfortabilities: values.rehabComfortabilities,
          entities: values.entities,
          cbsaPreferences: values.cbsaPreferences,
        },
      })
    );
  }

  handleRoles(values: string[]) {
    this.accountSetupForm.controls['roles'].setValue(values);
  }

  handleInvestmentStyles(values: string[]) {
    this.accountSetupForm.controls['investmentStyles'].setValue(values);
  }

  handleRehabComfortabilities(values: string[]) {
    this.accountSetupForm.controls['rehabComfortabilities'].setValue(values);
  }

  handleEntities(values: { key: string; value: string }[]) {
    const entities = values.map((entity) =>
      entity.key !== '-'
        ? {
            id: entity.key,
            full_name: entity.value,
          }
        : {
            full_name: entity.value,
          }
    );
    this.accountSetupForm.controls['entities'].setValue(entities);
  }

  handleCbsaPreferences(values: IFilterCBSAs[]) {
    this.accountSetupForm.controls['cbsaPreferences'].setValue(values);
  }

  checkMarketsCollapse(): void {
    if (this.marketsComponent) {
      this.marketsComponent.checkIfNeedsCollapse();
    }
  }

  goToStep(step: number): void {
    this.step = step;
    if (step === 2) this.checkMarketsCollapse();
  }
}
